import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, ChevronLeft, Play, Plus, Square } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ModuleTitleLIFT } from "@/components/lift/ModuleTitleLIFT";

export default function CycleAdmin() {
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", period_start: "", period_end: "", deadline: "" });
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lift/cycles");
      setCycles(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/lift/cycles", form);
      toast.success("Cycle created (draft).");
      setShowCreate(false);
      setForm({ name: "", period_start: "", period_end: "", deadline: "" });
      navigate(`/lift/cycles/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const activate = async (id) => {
    if (!window.confirm("Activate this cycle? Participants will be notified.")) return;
    try {
      await api.post(`/lift/cycles/${id}/activate`);
      toast.success("Cycle activated.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };
  const close = async (id) => {
    if (!window.confirm("Close this cycle?")) return;
    try {
      await api.post(`/lift/cycles/${id}/close`);
      toast.success("Cycle closed.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="lift-cycle-admin">
      <Link to="/lift" className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> LIFT home
      </Link>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <ModuleTitleLIFT />
          <h1 className="mt-3 text-4xl font-serif text-forest-900">Cycles</h1>
          <p className="mt-2 max-w-2xl text-charcoal-soft">
            Create and manage LIFT review cycles. Each cycle has its own templates, participants, and dashboard.
          </p>
        </div>
        <button className="patria-btn-primary" onClick={() => setShowCreate(true)} data-testid="lift-cycle-new">
          <Plus className="h-4 w-4" strokeWidth={1.5} /> New cycle
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl shadow-card">
        <table className="patria-table w-full">
          <thead>
            <tr>
              <th>Name</th>
              <th>Period</th>
              <th>Deadline</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="italic text-charcoal-muted">Loading…</td></tr>}
            {!loading && cycles.length === 0 && (
              <tr><td colSpan={5} className="italic text-charcoal-muted">No cycles yet — create one.</td></tr>
            )}
            {cycles.map((c) => (
              <tr key={c.id} data-testid={`lift-cycle-row-${c.id}`}>
                <td>
                  <button className="font-medium text-forest-900 hover:underline" onClick={() => navigate(`/lift/cycles/${c.id}`)}>
                    {c.name}
                  </button>
                </td>
                <td className="text-xs text-charcoal-muted">
                  {c.period_start} → {c.period_end}
                </td>
                <td className="whitespace-nowrap">{c.deadline}</td>
                <td>
                  <span className={c.status === "active" ? "patria-chip-green" : c.status === "closed" ? "patria-chip-neutral" : "patria-chip-terra"}>
                    {c.status}
                  </span>
                </td>
                <td className="text-right space-x-2 whitespace-nowrap">
                  {c.status === "draft" && (
                    <button className="patria-btn-ghost text-forest-700" onClick={() => activate(c.id)} data-testid={`lift-cycle-activate-${c.id}`}>
                      <Play className="h-4 w-4" strokeWidth={1.5} /> Activate
                    </button>
                  )}
                  {c.status === "active" && (
                    <button className="patria-btn-ghost text-terracotta-600" onClick={() => close(c.id)} data-testid={`lift-cycle-close-${c.id}`}>
                      <Square className="h-4 w-4" strokeWidth={1.5} /> Close
                    </button>
                  )}
                  <button className="patria-btn-ghost" onClick={() => navigate(`/lift/cycles/${c.id}`)}>
                    Manage
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-forest-900/40 flex items-center justify-center p-4">
          <div className="patria-card w-full max-w-lg p-6 space-y-4">
            <h3 className="text-2xl font-serif text-forest-900">New LIFT Cycle</h3>
            <div className="space-y-3">
              <FormRow label="Name" required>
                <input className="patria-input w-full" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lift-cycle-name" />
              </FormRow>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormRow label="Period start">
                  <input type="date" className="patria-input w-full" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="lift-cycle-period-start" />
                </FormRow>
                <FormRow label="Period end">
                  <input type="date" className="patria-input w-full" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="lift-cycle-period-end" />
                </FormRow>
              </div>
              <FormRow label="Completion deadline">
                <input type="date" className="patria-input w-full" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} data-testid="lift-cycle-deadline" />
              </FormRow>
            </div>
            <div className="flex justify-end gap-3">
              <button className="patria-btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
              <button className="patria-btn-primary" onClick={create} disabled={creating || !form.name || !form.deadline} data-testid="lift-cycle-create-submit">
                {creating ? "Creating..." : "Create draft"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FormRow({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1.5">
        {label} {required && <span className="text-terracotta-500">*</span>}
      </label>
      {children}
    </div>
  );
}
